// Powers the header search drawer.
//
// Uses the full storefront search via the Section Rendering API so that
// fields opted into Shopify Search & Discovery (e.g. metafields like
// custom.decor) are matched. Predictive search does not support metafield
// matching, which caused the drawer to miss products grouped only by
// metafield value.
const SECTION_ID = 'search-drawer-results';
const RESULTS_NODE_ID = 'search-drawer-results';
const DEBOUNCE_MS = 300;

let activeController = null;
let debounceTimer = null;

const getElements = () => {
  const root = document.querySelector('[data-search-drawer]');
  if (!root) return null;
  return {
    root,
    input: root.querySelector('[data-search-input]'),
    results: root.querySelector('[data-search-results]'),
    clear: root.querySelector('[data-search-clear]'),
    status: root.querySelector('[data-search-status]'),
    viewAll: root.querySelector('[data-search-view-all]'),
    form: root.querySelector('[data-search-form]'),
    searchUrl: root.dataset.searchUrl,
  };
};

const fetchResults = async (els, query) => {
  if (activeController) activeController.abort();
  activeController = new AbortController();

  const url = new URL(els.searchUrl, location.origin);
  url.searchParams.set('q', query);
  url.searchParams.set('type', 'product');
  url.searchParams.set('options[prefix]', 'last');
  url.searchParams.set('section_id', SECTION_ID);

  const response = await fetch(url.toString(), { signal: activeController.signal });
  if (!response.ok) throw new Error(`Search failed: ${response.status}`);

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.getElementById(RESULTS_NODE_ID);
};

const showClear = (els) => {
  if (els.clear) els.clear.hidden = false;
};

const hideClear = (els) => {
  if (els.clear) els.clear.hidden = true;
};

const updateViewAll = (els, query) => {
  if (!els.viewAll) return;
  if (query) {
    const url = new URL(els.searchUrl, location.origin);
    url.searchParams.set('q', query);
    els.viewAll.href = url.toString();
    els.viewAll.hidden = false;
  } else {
    els.viewAll.hidden = true;
  }
};

const handleInput = (els) => {
  clearTimeout(debounceTimer);

  const query = els.input.value.trim();

  if (!query) {
    resetSearch(els);
    return;
  }

  showClear(els);

  if (els.status) {
    els.status.hidden = false;
    els.results.innerHTML = '';
  }

  debounceTimer = setTimeout(async () => {
    try {
      const resultNode = await fetchResults(els, query);
      if (resultNode) {
        els.results.innerHTML = resultNode.innerHTML;
        els.results.classList.add('has-results');
      } else {
        els.results.innerHTML = '';
        els.results.classList.remove('has-results');
      }
      updateViewAll(els, query);
    } catch (err) {
      if (err.name === 'AbortError') return;
      els.results.innerHTML = '';
      els.results.classList.remove('has-results');
    } finally {
      if (els.status) els.status.hidden = true;
    }
  }, DEBOUNCE_MS);
};

const resetSearch = (els) => {
  clearTimeout(debounceTimer);
  if (activeController) activeController.abort();
  els.input.value = '';
  els.results.innerHTML = '';
  els.results.classList.remove('has-results');
  hideClear(els);
  updateViewAll(els, '');
  if (els.status) els.status.hidden = true;
  els.input.focus();
};

const handleKeyDown = (event, els) => {
  if (event.key === 'Escape') {
    event.preventDefault();
    if (els.input.value.trim()) {
      resetSearch(els);
    }
  }
};

const init = () => {
  const els = getElements();
  if (!els?.input || !els.searchUrl) return;

  els.input.addEventListener('input', () => handleInput(els));
  els.input.addEventListener('keydown', (e) => handleKeyDown(e, els));

  if (els.clear) {
    els.clear.addEventListener('click', () => resetSearch(els));
  }

  document.addEventListener('drawer:opened', (e) => {
    if (e.detail?.id === 'search-drawer') {
      requestAnimationFrame(() => els.input.focus());
    }
  });

  document.addEventListener('drawer:closed', (e) => {
    if (e.detail?.id === 'search-drawer') {
      resetSearch(els);
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
